/**
 * ESTADO GLOBAL: SESIÓN Y BRANDING
 *
 * Sólo dos cosas son globales de verdad, y son éstas. Todo lo demás —los
 * datos de cada pantalla— se pide donde se usa y no se guarda en ningún
 * almacén compartido: un caché global es la forma más rápida de enseñar
 * datos de un centro mientras se está mirando otro.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  api,
  CLAVE_TOKEN,
  cuandoCaduqueLaSesion,
  FalloApi,
  guardarToken,
  tokenGuardado,
  type Branding,
  type Usuario,
} from './api.ts';
import { ToastProvider } from './componentes/toast.tsx';
import { tieneCapacidadOffline } from './offline/paquete.ts';
import {
  bloquearPerfilActivo,
  estadoBloqueoActual,
  fingerprintDeUsuario,
  perfilActivo,
  suscribirEstadoBloqueo,
} from './offline/perfil.ts';
import { fijarUsuarioDeSesion } from './offline/identidad-sync.ts';
import { activarSincronizacionAutomatica } from './offline/sync.ts';

interface Sesion {
  readonly usuario: Usuario | null;
  readonly cargando: boolean;
  /**
   * Estabilización post-F13: la sesión guardada no se pudo COMPROBAR (servidor caído, 5xx, 429, sin red) — no es lo
   * mismo que «no hay sesión». El token se conserva y se ofrece reintentar; sólo un 401 manda al login.
   */
  readonly errorComprobacion: FalloApi | null;
  readonly reintentarComprobacion: () => void;
  readonly entrar: (email: string, password: string) => Promise<void>;
  readonly salir: () => Promise<void>;
  readonly puede: (permiso: string) => boolean;
  /** Refresca `usuario` desde /api/auth/me — tras cambiar la contraseña, `requiereCambioPwd` pasa a false. */
  readonly cambiarPassword: (passwordActual: string, passwordNueva: string) => Promise<void>;
}

const ContextoSesion = createContext<Sesion | null>(null);
const ContextoBranding = createContext<Branding | null>(null);

/** Respaldo mientras carga el branding. Nunca se ve una pantalla rota. */
const BRANDING_PROVISIONAL: Branding = {
  nombre: 'NeatOS',
  nombreCorto: 'NeatOS',
  lema: '',
  organizacion: 'NeatOS',
  colorPrincipal: '#0f6ea8',
  colorOscuro: '#12242f',
  logoUrl: '',
  faviconUrl: '',
  monograma: 'N',
};

export function Proveedores({ children }: { children: ReactNode }): ReactNode {
  const [branding, setBranding] = useState<Branding>(BRANDING_PROVISIONAL);
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [cargando, setCargando] = useState(true);
  const [errorComprobacion, setErrorComprobacion] = useState<FalloApi | null>(null);
  const [intentoComprobacion, setIntentoComprobacion] = useState(0);

  //  El branding manda sobre el título y sobre los dos colores. Se aplica
  //  como variables CSS: no hay hoja de estilos por cliente.
  useEffect(() => {
    let vivo = true;
    void api
      .get<Branding>('/api/branding')
      .then((b) => {
        if (vivo) setBranding(b);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    document.title = branding.nombre;
    const raiz = document.documentElement;
    raiz.style.setProperty('--marca', branding.colorPrincipal);
    raiz.style.setProperty('--marca-oscuro', branding.colorOscuro);
    //  UX-1D: por defecto la pestaña usa el isotipo de NeatOS (index.html). Un tenant con
    //  BRAND_FAVICON_URL propio lo sustituye aquí; sin él, no se toca nada.
    if (typeof branding.faviconUrl === 'string' && branding.faviconUrl !== '') {
      for (const enlace of document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')) {
        enlace.href = branding.faviconUrl;
        enlace.removeAttribute('type');
        enlace.removeAttribute('sizes');
      }
    }
  }, [branding]);

  //  Sesión guardada: se comprueba contra el servidor antes de darla por
  //  buena. Un token en `localStorage` no prueba nada por sí solo.
  useEffect(() => {
    if (tokenGuardado() === null) {
      setCargando(false);
      return;
    }
    let vivo = true;
    setCargando(true);
    setErrorComprobacion(null);
    void api
      .get<Usuario>('/api/auth/me')
      .then((u) => {
        if (vivo) setUsuario(u);
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        setUsuario(null);
        //  Un 401 ya borró el token (api.ts): no hay sesión. Cualquier otro fallo (5xx, 429, red) NO dice que la
        //  sesión haya terminado: antes también mandaba al login, y tras un reinicio del servidor o un 502 del
        //  proxy parecía que «me ha echado».
        if (!(e instanceof FalloApi && e.estado === 401) && tokenGuardado() !== null) {
          setErrorComprobacion(e instanceof FalloApi ? e : new FalloApi(0, { code: 'ERROR', message: 'No se ha podido comprobar la sesión.' }));
        }
      })
      .finally(() => {
        if (vivo) setCargando(false);
      });
    return () => {
      vivo = false;
    };
  }, [intentoComprobacion]);

  useEffect(() => cuandoCaduqueLaSesion(() => setUsuario(null)), []);

  //  F14.1 — si la sesión no se pudo comprobar por falta de red (recarga sin conexión), se vuelve a comprobar sola en
  //  cuanto vuelve la conexión: sin esto, quien trabajó offline seguía «sin usuario» tras reconectar.
  useEffect(() => {
    if (errorComprobacion === null) return;
    const reintentar = (): void => setIntentoComprobacion((n) => n + 1);
    window.addEventListener('online', reintentar);
    window.addEventListener('neatos:conexion-recuperada', reintentar);
    return () => {
      window.removeEventListener('online', reintentar);
      window.removeEventListener('neatos:conexion-recuperada', reintentar);
    };
  }, [errorComprobacion]);

  //  El token vive en localStorage, común a todas las pestañas del navegador. Si otra pestaña sale o entra con otra
  //  cuenta, ésta seguía pintando el usuario anterior con el token del otro (y la siguiente petición «echaba» sin
  //  explicación). Ahora se sigue el cambio: sin token → sin sesión; token distinto → se vuelve a leer quién es.
  useEffect(() => {
    function alCambiar(ev: StorageEvent): void {
      if (ev.key !== CLAVE_TOKEN && ev.key !== null) return;
      if (tokenGuardado() === null) {
        setUsuario(null);
        return;
      }
      void api.get<Usuario>('/api/auth/me').then(setUsuario).catch(() => undefined);
    }
    window.addEventListener('storage', alCambiar);
    return () => window.removeEventListener('storage', alCambiar);
  }, []);

  //  FASE 5.3 §16/§17 + FASE 5.6 §17 — se activa UNA vez por sesión, aquí y
  //  no en cada pantalla: `Proveedores` envuelve el router entero y nunca
  //  se desmonta al navegar, así que esto sólo se reactiva de verdad al
  //  entrar/salir de sesión, nunca por visitar `/offline` otra vez desde
  //  dentro de la SPA — eso sería confundir "el usuario pulsó un enlace"
  //  con "se reabrió la aplicación". Acotado a quien tiene capacidad
  //  offline (ENCARGADO/OPERARIO): para el resto la cola sincronizable
  //  siempre está vacía.
  //
  //  DESDE 5.6, además, nunca sincroniza con el perfil LOCKED (§17: "si
  //  perfil LOCKED: NO sync") — el paquete/cola local vive cifrado, y sin
  //  DEK en memoria no hay nada que leer para construir un lote. Este
  //  efecto se suscribe al estado de bloqueo (`perfil.ts`) y activa/
  //  desactiva `activarSincronizacionAutomatica` cada vez que cambia,
  //  usando SIEMPRE el `perfilId` del perfil desbloqueado — nunca
  //  `usuario.id` (que ya no es el identificador del almacén offline).
  //  Comprueba también que el perfil desbloqueado sea el de ESTE usuario
  //  (huella): un perfil de OTRO usuario que siguiera desbloqueado por
  //  error de programación en otro sitio nunca dispara sync aquí.
  //  F14.2 — la identidad con la que el motor de sincronización compara el perfil offline (`identidad-sync.ts`).
  //  Declarado ANTES que el disparo automático de abajo: los efectos corren en orden.
  useEffect(() => {
    fijarUsuarioDeSesion(usuario?.id ?? null);
  }, [usuario]);

  useEffect(() => {
    if (usuario === null || !tieneCapacidadOffline(usuario.roles)) return;
    const idUsuario = usuario.id; // copia no-nula: TS no propaga el `usuario !== null` de arriba dentro de `evaluar`, una función anidada que se invoca más tarde.
    let cancelarActual: (() => void) | null = null;
    let vivo = true;

    function detener(): void {
      cancelarActual?.();
      cancelarActual = null;
    }

    async function evaluar(): Promise<void> {
      detener();
      const estado = estadoBloqueoActual();
      if (estado?.estado !== 'UNLOCKED') return;
      const activo = perfilActivo();
      if (activo === null) return;
      const huella = await fingerprintDeUsuario(idUsuario);
      if (!vivo || activo.fingerprintUsuario !== huella) return;
      cancelarActual = activarSincronizacionAutomatica(estado.perfilId);
    }

    void evaluar();
    const cancelarSuscripcion = suscribirEstadoBloqueo(() => void evaluar());
    return () => {
      vivo = false;
      cancelarSuscripcion();
      detener();
    };
  }, [usuario]);

  const entrar = useCallback(async (email: string, password: string) => {
    const r = await api.post<{ token: string; usuario: Usuario }>('/api/auth/login', {
      email,
      password,
    });
    guardarToken(r.token);
    setErrorComprobacion(null);
    //  El login devuelve el usuario sin permisos calculados; se piden a
    //  `/me` para tener una sola fuente de qué puede hacer.
    setUsuario(await api.get<Usuario>('/api/auth/me'));
  }, []);

  const salir = useCallback(async () => {
    //  FASE 5.7-C C01 — el cierre de sesión LOCAL nunca depende del
    //  backend: si estuviera offline o con la red caída, un `await` aquí
    //  antes de limpiar dejaba el logout colgado indefinidamente (ninguna
    //  llamada de `api.ts` tenía antes un límite de tiempo). Todo lo de
    //  abajo (tras esta llamada) es síncrono/inmediato; el aviso al
    //  servidor sigue en segundo plano, sin bloquear el resultado de esta
    //  función.
    //
    //  CRÍTICO: `api.post` lee el token guardado EN EL MOMENTO en que se
    //  invoca (síncrono, antes de su primer `await` interno) — así que
    //  esta llamada tiene que hacerse ANTES de `guardarToken(null)`, no
    //  después. Invertir el orden (como hizo una primera versión de este
    //  cambio) manda el aviso de logout SIN cabecera `authorization`: el
    //  backend responde 401 y nunca invalida la sesión del lado servidor
    //  — regresión real, detectada por los E2E de este mismo repaso.
    const controlador = new AbortController();
    const limite = setTimeout(() => controlador.abort(), 5000);
    void api
      .post('/api/auth/logout', undefined, { signal: controlador.signal })
      .catch(() => undefined)
      .finally(() => clearTimeout(limite));

    guardarToken(null);
    setUsuario(null);
    setErrorComprobacion(null);
    //  FASE 5.6 §15 — logout bloquea el perfil offline (borra la DEK de
    //  memoria) pero NUNCA borra su ciphertext: mutaciones, blobs y
    //  paquete siguen intactos, cifrados, esperando el PIN la próxima
    //  vez. El propio cambio de `usuario` a `null` ya desactiva el efecto
    //  de sincronización automática (dependencia `[usuario]`, más abajo)
    //  — no hace falta cancelarlo aparte.
    bloquearPerfilActivo();
  }, []);

  const cambiarPassword = useCallback(async (passwordActual: string, passwordNueva: string) => {
    await api.post('/api/auth/cambiar-password', { passwordActual, passwordNueva });
    //  Misma razón que en `entrar`: una sola fuente de verdad para el
    //  estado de la sesión. `requiereCambioPwd` sólo se da por resuelto
    //  cuando el propio servidor lo confirma en la siguiente lectura.
    setUsuario(await api.get<Usuario>('/api/auth/me'));
  }, []);

  const sesion = useMemo<Sesion>(
    () => ({
      usuario,
      cargando,
      errorComprobacion,
      reintentarComprobacion: () => setIntentoComprobacion((n) => n + 1),
      entrar,
      salir,
      puede: (permiso) => usuario?.permisos.includes(permiso) ?? false,
      cambiarPassword,
    }),
    [usuario, cargando, errorComprobacion, entrar, salir, cambiarPassword],
  );

  return (
    <ContextoBranding.Provider value={branding}>
      {/* UX-1C: feedback ligero (toasts), común a toda la aplicación. */}
      <ContextoSesion.Provider value={sesion}><ToastProvider>{children}</ToastProvider></ContextoSesion.Provider>
    </ContextoBranding.Provider>
  );
}

/** F14.1 — para lo que se monta también fuera de `<Proveedores>` (avisos globales en pruebas aisladas): `null` en vez de lanzar. */
export function useSesionOpcional(): Sesion | null {
  return useContext(ContextoSesion);
}

export function useSesion(): Sesion {
  const c = useContext(ContextoSesion);
  if (c === null) throw new Error('useSesion fuera de <Proveedores>.');
  return c;
}

export function useBranding(): Branding {
  return useContext(ContextoBranding) ?? BRANDING_PROVISIONAL;
}
